/**
 * The one place that decides whether a remembered destination may be navigated to.
 *
 * `RequireSession` stores where a person was going when the portal stopped them, and the sign-in
 * screen and the verification screen read it back. That value travels through router state, which
 * means it is a value the application did not author: anything that can push a history entry can put
 * something there, and a link with a crafted state is a link somebody can be sent. A redirect target
 * taken on trust is an open redirect, and an open redirect on a sign-in screen is the classic
 * phishing shape: the customer signs in on the real portal, at the real domain, and is then handed
 * to a page that is not ours while still believing they are inside it.
 *
 * So the rule is an allow-list rather than a deny-list. One thing is accepted, an absolute path
 * inside this application, and everything else is refused without trying to repair it. A guard that
 * sanitises rather than refuses is a guard that ships whatever it failed to think of.
 *
 * The parsing is done by the URL parser rather than by string tests, because the string tests are
 * what people get wrong. `//evil.example` starts with a slash and leaves the site. `/\evil.example`
 * is the same thing once the browser has done its own normalisation, since a backslash is a slash in
 * a special scheme. Resolving against a base and comparing the origin catches both without needing
 * to enumerate them.
 */

/**
 * A base that exists only to be compared against. Nothing is fetched, and the hostname is in the
 * reserved `.invalid` TLD so it can never resolve anywhere even by accident.
 */
const BASE = "http://portal.invalid";

/**
 * Screens that are part of signing in rather than destinations to be returned to.
 *
 * Sending somebody back to `/login` after they have just signed in would bounce them straight out
 * again, and `/verify` carries the destination onwards itself rather than being one.
 */
const NOT_A_DESTINATION = ["/login", "/verify"];

export function safeInternalPath(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0) {
    return null;
  }

  /**
   * Tabs, newlines and carriage returns are removed by browsers before a URL is resolved, so
   * `/<tab>/evil.example` is `//evil.example` by the time it means anything. Refusing the string
   * outright is safer than stripping them and reasoning about what is left.
   */
  if (/[\t\n\r]/.test(value)) {
    return null;
  }

  // An internal destination is an absolute path. A relative one would resolve against wherever the
  // reader happens to be standing, which is not a destination anybody chose.
  if (!value.startsWith("/")) {
    return null;
  }

  let url: URL;

  try {
    url = new URL(value, BASE);
  } catch {
    return null;
  }

  // The whole test. Anything that reached another origin, by scheme, by authority or by a backslash
  // the parser normalised, fails here.
  if (url.origin !== BASE) {
    return null;
  }

  if (NOT_A_DESTINATION.some((path) => url.pathname === path || url.pathname.startsWith(`${path}/`))) {
    return null;
  }

  // Path, query and fragment together, because the destination is rarely only the path. A
  // notification points at a request with a tab open, and dropping everything after the pathname
  // lands the person on the right screen and the wrong part of it.
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Where an admin lands after signing in with nowhere else remembered. The inbox on `/admin/clients`
 * since 2026-09-22; until then it was the request board, which is where an ärende is worked and not
 * where a day starts, and it left every client message one click away from the first screen.
 *
 * `/admin/inbox` since 2026-09-24, when the build round after the sketches gave the inbox its own
 * page and made it the first of the five destinations.
 */
export const ADMIN_HOME = "/admin/inbox";
